import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { signersApi } from '@/lib/api/signers';
import { usersApi } from '@/lib/api/users';
import type { SignerDto, UpdateSignerRequest } from '@paladin-monorepo/common';

type FieldsForm = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  phone: string;
};

function signerHasRequiredFields(s: SignerDto): boolean {
  return [s.firstName, s.lastName, s.jobTitle, s.email, s.phone].every(
    (v) => typeof v === 'string' && v.trim() !== '',
  );
}

/** Opened from contracts sidebar to create a new signer and assign to the document (manual form only). */
export type DocumentSignerCreateContext = {
  party: 'inquirer' | 'supplier';
};

interface SignerUpsertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signer: SignerDto | null;
  /** When set with `signer == null`, only manual create is shown; existing signers are chosen in the sidebar. */
  documentSignerCreate?: DocumentSignerCreateContext | null;
  /** When create-from-user returns a person missing any required field, parent switches dialog to edit. */
  onSignerCreatedIncomplete?: (signer: SignerDto) => void;
  /** Called after a successful create (complete signer); dialog closes after this. */
  onCreated?: (signer: SignerDto) => void;
}

export function SignerUpsertDialog({
  open,
  onOpenChange,
  signer,
  documentSignerCreate = null,
  onSignerCreatedIncomplete,
  onCreated,
}: SignerUpsertDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!signer;
  const isDocumentSignerCreate = !!documentSignerCreate && !signer;
  const [createSource, setCreateSource] = useState<'blank' | 'user'>('blank');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const lastCreateWasFromUser = useRef(false);

  const fieldsSchema = yup.object({
    firstName: yup.string().required(t('signers.dialog.form.firstName.errors.required')),
    lastName: yup.string().required(t('signers.dialog.form.lastName.errors.required')),
    jobTitle: yup.string().required(t('signers.dialog.form.jobTitle.errors.required')),
    email: yup
      .string()
      .email(t('signers.dialog.form.email.errors.invalid'))
      .required(t('signers.dialog.form.email.errors.required')),
    phone: yup.string().required(t('signers.dialog.form.phone.errors.required')),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FieldsForm>({
    resolver: yupResolver(fieldsSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      jobTitle: '',
      email: '',
      phone: '',
    },
  });

  const { data: accountUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: open && !isEdit && !isDocumentSignerCreate,
  });

  useEffect(() => {
    if (!open) return;
    if (signer) {
      reset({
        firstName: signer.firstName,
        lastName: signer.lastName,
        jobTitle: signer.jobTitle || '',
        email: signer.email,
        phone: signer.phone || '',
      });
      return;
    }
    reset({
      firstName: '',
      lastName: '',
      jobTitle: '',
      email: '',
      phone: '',
    });
    setCreateSource('blank');
    setSelectedUserId('');
  }, [open, signer, reset, documentSignerCreate?.party]);

  useEffect(() => {
    if (!open) {
      lastCreateWasFromUser.current = false;
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: signersApi.create,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['signers'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      const fromUser = lastCreateWasFromUser.current;
      lastCreateWasFromUser.current = false;
      if (fromUser && !signerHasRequiredFields(created) && onSignerCreatedIncomplete) {
        toast.success(t('signers.dialog.successCreateIncomplete'));
        onSignerCreatedIncomplete(created);
        return;
      }
      toast.success(t('signers.dialog.successCreate'));
      onCreated?.(created);
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t('signers.dialog.error'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateSignerRequest }) =>
      signersApi.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signers'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(t('signers.dialog.successEdit'));
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t('signers.dialog.error'));
    },
  });

  const pending = createMutation.isPending || updateMutation.isPending;

  const onSubmitFields = (data: FieldsForm) => {
    if (isEdit && signer) {
      updateMutation.mutate({
        id: signer.id,
        body: {
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          jobTitle: data.jobTitle.trim(),
          email: data.email.trim(),
          phone: data.phone.trim(),
        },
      });
      return;
    }
    lastCreateWasFromUser.current = false;
    createMutation.mutate({
      mode: 'blank',
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      jobTitle: data.jobTitle.trim(),
      email: data.email.trim(),
      phone: data.phone.trim(),
    });
  };

  const submitFromUser = () => {
    if (!selectedUserId) {
      toast.error(t('signers.dialog.errors.userRequired'));
      return;
    }
    lastCreateWasFromUser.current = true;
    createMutation.mutate({ mode: 'user', userId: selectedUserId });
  };

  const showFieldForm = isEdit || createSource === 'blank' || isDocumentSignerCreate;
  const party = documentSignerCreate?.party;

  const dialogTitle = (() => {
    if (isEdit) return t('signers.dialog.editTitle');
    return t('contracts.signers.newSignerDialog.title');
  })();

  const dialogDescription = (() => {
    if (isEdit) return t('signers.dialog.editDescription');
    if (isDocumentSignerCreate) {
      return t('contracts.signers.newSignerDialog.description');
    }
    return t('signers.dialog.createDescription');
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {!isEdit && !isDocumentSignerCreate && (
          <div className="space-y-2">
            <Label>{t('signers.dialog.createModeLabel')}</Label>
            <Select
              value={createSource}
              onValueChange={(v) => {
                setCreateSource(v as 'blank' | 'user');
                setSelectedUserId('');
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blank">{t('signers.dialog.modeBlank')}</SelectItem>
                <SelectItem value="user">{t('signers.dialog.modeUser')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {!isEdit && !isDocumentSignerCreate && createSource === 'user' && (
          <div className="space-y-2">
            <Label>{t('signers.dialog.userSelect')}</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('signers.dialog.userPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {accountUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email} ({u.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('generic.cancel')}
              </Button>
              <Button type="button" onClick={submitFromUser} disabled={pending}>
                {pending ? t('generic.loading') : t('signers.dialog.submitCreate')}
              </Button>
            </DialogFooter>
          </div>
        )}

        {showFieldForm && (
          <form onSubmit={handleSubmit(onSubmitFields)} className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="sig-fn">{t('signers.dialog.form.firstName.label')}</Label>
              <Input id="sig-fn" {...register('firstName')} />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sig-ln">{t('signers.dialog.form.lastName.label')}</Label>
              <Input id="sig-ln" {...register('lastName')} />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sig-jt">{t('signers.dialog.form.jobTitle.label')}</Label>
              <Input id="sig-jt" {...register('jobTitle')} />
              {errors.jobTitle && (
                <p className="text-sm text-destructive">{errors.jobTitle.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sig-em">{t('signers.dialog.form.email.label')}</Label>
              <Input id="sig-em" type="email" {...register('email')} />
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sig-ph">{t('signers.dialog.form.phone.label')}</Label>
              <Input id="sig-ph" {...register('phone')} />
              {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('generic.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending
                  ? t('generic.loading')
                  : isEdit
                    ? t('signers.dialog.submitEdit')
                    : t('signers.dialog.submitCreate')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
